<!-- Editable Mode (for modal) -->
<div *ngIf="editableMode" class="editable-metadata-form">
  <div class="step-content" *ngIf="metadataForm && additionalInfoForm">
    <!-- Update metadata from a Croissant JSON file (datasets only) -->
    <div *ngIf="!isModel()" class="croissant-update">
      <div class="croissant-update__text">
        <h4 class="croissant-update__title">Update from JSON file</h4>
        <p class="croissant-update__hint">
          Upload a Croissant metadata JSON to fill this form. Review the values, then click Save to update the asset.
        </p>
        <p *ngIf="uploadedCroissantFileName" class="croissant-update__file">
          Loaded: <strong>{{ uploadedCroissantFileName }}</strong>
          <button type="button" class="croissant-update__clear" (click)="clearUploadedCroissant()">Remove</button>
        </p>
      </div>
      <button type="button" class="croissant-update__btn" (click)="croissantFileInput.click()">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
          <polyline points="17 8 12 3 7 8"/>
          <line x1="12" y1="3" x2="12" y2="15"/>
        </svg>
        {{ uploadedCroissantFileName ? 'Upload another' : 'Upload JSON' }}
      </button>
      <input #croissantFileInput type="file" accept=".json,application/json" hidden (change)="onCroissantFileSelected($event)" />
      <div *ngIf="croissantUploadErrors.length > 0" class="croissant-update__errors">
        <p>The file could not be used:</p>
        <ul>
          <li *ngFor="let error of croissantUploadErrors">{{ error }}</li>
        </ul>
      </div>
      <div *ngIf="croissantUploadWarnings.length > 0" class="croissant-update__warnings">
        <ul>
          <li *ngFor="let warning of croissantUploadWarnings">{{ warning }}</li>
        </ul>
      </div>
    </div>

    <!-- Dataset Information Fields (Title and Permission: editable only in modify mode) -->
    <div *ngIf="datasetData" class="form-fields">
      <div class="form-row">
        <div class="form-field col-6">
          <label for="dataset-title" class="form-label">
            Title <span class="required">*</span>
          </label>
          <input
            type="text"
            id="dataset-title"
            [value]="datasetData.title"
            class="form-input"
            [disabled]="!allowSaveWithoutDirty"
            [readOnly]="!allowSaveWithoutDirty"
            (input)="onTitleInput($event)"
          />
        </div>
        <div class="form-field col-6">
          <label for="dataset-permission" class="form-label">
            Permission <span class="required">*</span>
          </label>
          <!-- In modify mode (allowSaveWithoutDirty), use same custom dropdown style as create flow -->
          <ng-container *ngIf="allowSaveWithoutDirty; else readonlyPermission">
            <div class="custom-dropdown-wrapper">
              <div
                class="custom-dropdown-trigger"
                [class.open]="isPermissionDropdownOpen"
                (click)="togglePermissionDropdown()"
              >
                <div class="trigger-content">
                  <span
                    *ngIf="!datasetData?.permission"
                    class="placeholder"
                  >
                    Select permission
                  </span>
                  <span
                    *ngIf="datasetData?.permission"
                    class="permission-chip"
                    [class.permission-chip--public]="datasetData?.permission === 'Open'"
                    [class.permission-chip--restricted]="datasetData?.permission === 'Restricted'"
                  >
                    <img src="/public.svg" alt="" class="permission-chip__icon" aria-hidden="true" />
                    {{ getSelectedPermissionLabelForEdit() }}
                  </span>
                </div>
                <svg
                  class="dropdown-arrow"
                  [class.rotated]="isPermissionDropdownOpen"
                  width="12"
                  height="8"
                  viewBox="0 0 12 8"
                  fill="none"
                >
                  <path d="M1 1L6 6L11 1" stroke="#666" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <div
                *ngIf="isPermissionDropdownOpen"
                class="custom-dropdown-panel"
                (click)="$event.stopPropagation()"
              >
                <div
                  class="dropdown-option"
                  (click)="selectPermissionOption('Open')"
                >
                  <div class="checkbox-wrapper">
                    <input
                      type="checkbox"
                      [checked]="datasetData?.permission === 'Open'"
                      class="checkbox-custom"
                      readonly
                    />
                  </div>
                  <span class="option-label">Public</span>
                </div>
                <div
                  class="dropdown-option"
                  (click)="selectPermissionOption('Restricted')"
                >
                  <div class="checkbox-wrapper">
                    <input
                      type="checkbox"
                      [checked]="datasetData?.permission === 'Restricted'"
                      class="checkbox-custom"
                      readonly
                    />
                  </div>
                  <span class="option-label">Restricted</span>
                </div>
              </div>
            </div>
          </ng-container>
          <ng-template #readonlyPermission>
            <input
              type="text"
              id="dataset-permission"
              [value]="getPermissionDisplayLabel(datasetData.permission)"
              class="form-input"
              disabled
              readonly
            />
          </ng-template>
        </div>
      </div>
      <hr class="form-divider" />
    </div>

    <!-- Metadata Form (Editable) -->
    <form [formGroup]="metadataForm">
      <!-- For Datasets -->
      <ng-container *ngIf="!isModel()">
        <!--
          Short/Long Description, Year Range, Geo Coverage, Resource Server, Upload Frequency,
          License and Industry are intentionally not shown here — these administrative catalogue
          fields aren't central to editing a TANUH benchmark dataset. Their form controls still
          exist (see initializeForms()) and are auto-filled on save: existing values are carried
          forward unchanged, and only a genuinely blank field gets a templated placeholder so the
          backend's required-field validation still passes (see applyHiddenMandatoryDefaults()).
        -->

        <!-- Dataset characteristics -->
        <section class="dataset-details-section" aria-labelledby="modify-dataset-details-heading">
          <h3 id="modify-dataset-details-heading" class="dataset-details-section__title">Dataset characteristics</h3>
          <div class="dataset-details-grid">
            <div
              *ngFor="let field of datasetDetailFields"
              class="dataset-detail-field"
              [class.dataset-detail-field--filled]="isDetailFieldFilled(field.key)"
              [class.dataset-detail-field--invalid]="isDetailFieldInvalid(field.key)"
            >
              <label [for]="field.key" class="dataset-detail-field__label">
                {{ field.label }} <span class="required">*</span>
              </label>
              <input
                type="text"
                [id]="field.key"
                [formControlName]="field.key"
                class="dataset-detail-field__input"
                [placeholder]="field.placeholder"
                [readonly]="!editableMode"
                [class.error]="isDetailFieldInvalid(field.key)"
              />
              <div *ngIf="isDetailFieldInvalid(field.key)" class="dataset-detail-field__error">
                {{ getDetailFieldError(field.key) }}
              </div>
            </div>
          </div>
        </section>

        <!-- Input specification -->
        <section class="dataset-details-section" aria-labelledby="modify-dataset-input-heading">
          <h3 id="modify-dataset-input-heading" class="dataset-details-section__title">Input</h3>
          <div class="dataset-details-grid">
            <div
              *ngFor="let field of datasetInputFields"
              class="dataset-detail-field"
              [class.dataset-detail-field--filled]="isDetailFieldFilled(field.key)"
              [class.dataset-detail-field--invalid]="isDetailFieldInvalid(field.key)"
              [class.dataset-detail-field--wide]="field.type === 'textarea'"
            >
              <label [for]="field.key" class="dataset-detail-field__label">
                {{ field.label }}
                <span *ngIf="isFieldRequired(field)" class="required">*</span>
              </label>
              <textarea
                *ngIf="field.type === 'textarea'; else modifyInputFieldControl"
                [id]="field.key"
                [formControlName]="field.key"
                class="dataset-detail-field__textarea"
                rows="3"
                [placeholder]="field.placeholder"
                [readonly]="!editableMode"
                [class.error]="isDetailFieldInvalid(field.key)"
              ></textarea>
              <ng-template #modifyInputFieldControl>
                <input
                  type="text"
                  [id]="field.key"
                  [formControlName]="field.key"
                  class="dataset-detail-field__input"
                  [placeholder]="field.placeholder"
                  [readonly]="!editableMode"
                  [class.error]="isDetailFieldInvalid(field.key)"
                />
              </ng-template>
              <div *ngIf="isDetailFieldInvalid(field.key)" class="dataset-detail-field__error">
                {{ getDetailFieldError(field.key) }}
              </div>
            </div>
          </div>
        </section>

        <!-- Output specification -->
        <section class="dataset-details-section" aria-labelledby="modify-dataset-output-heading">
          <h3 id="modify-dataset-output-heading" class="dataset-details-section__title">Output</h3>
          <div class="dataset-details-grid">
            <div
              *ngFor="let field of datasetOutputFields"
              class="dataset-detail-field"
              [class.dataset-detail-field--filled]="isDetailFieldFilled(field.key)"
              [class.dataset-detail-field--invalid]="isDetailFieldInvalid(field.key)"
              [class.dataset-detail-field--wide]="field.type === 'textarea'"
            >
              <label [for]="field.key" class="dataset-detail-field__label">
                {{ field.label }}
                <span *ngIf="isFieldRequired(field)" class="required">*</span>
              </label>
              <textarea
                *ngIf="field.type === 'textarea'; else modifyOutputFieldControl"
                [id]="field.key"
                [formControlName]="field.key"
                class="dataset-detail-field__textarea"
                rows="3"
                [placeholder]="field.placeholder"
                [readonly]="!editableMode"
                [class.error]="isDetailFieldInvalid(field.key)"
              ></textarea>
              <ng-template #modifyOutputFieldControl>
                <input
                  type="text"
                  [id]="field.key"
                  [formControlName]="field.key"
                  class="dataset-detail-field__input"
                  [placeholder]="field.placeholder"
                  [readonly]="!editableMode"
                  [class.error]="isDetailFieldInvalid(field.key)"
                />
              </ng-template>
              <div *ngIf="isDetailFieldInvalid(field.key)" class="dataset-detail-field__error">
                {{ getDetailFieldError(field.key) }}
              </div>
            </div>
          </div>
        </section>

        <!-- Benchmark metrics (name + expected min/max range) -->
        <section class="dataset-metrics-section" aria-labelledby="modify-dataset-metrics-heading" formArrayName="customMetrics">
          <div class="dataset-metrics-section__header">
            <h3 id="modify-dataset-metrics-heading" class="dataset-metrics-section__title">Benchmark metrics</h3>
            <p class="dataset-metrics-section__subtitle">
              Name the metrics your benchmark reports. Values are captured later, when a model is evaluated.
            </p>
          </div>
          <div class="metrics-grid">
            <div
              *ngFor="let metric of customMetrics.controls; let i = index"
              [formGroupName]="i"
              class="metric-field-card"
              [class.metric-field-card--filled]="isCustomMetricFilled(i)"
              [class.metric-field-card--invalid]="isCustomMetricInvalid(i)"
            >
              <label [for]="'modify-custom-metric-' + i" class="metric-field-card__label">
                <span class="metric-field-card__index">{{ i + 1 }}</span>
                <span class="metric-field-card__text">Metric name</span>
              </label>
              <div class="metric-field-card__input-wrap">
                <input
                  type="text"
                  [id]="'modify-custom-metric-' + i"
                  formControlName="name"
                  class="metric-field-card__input"
                  placeholder="e.g. Accuracy"
                  [readonly]="!editableMode"
                  [class.error]="isCustomMetricInvalid(i)"
                />
              </div>
              <div class="metric-field-card__range">
                <div class="metric-field-card__range-field">
                  <label [for]="'modify-custom-metric-min-' + i" class="metric-field-card__range-label">Min</label>
                  <input
                    type="text"
                    inputmode="decimal"
                    [id]="'modify-custom-metric-min-' + i"
                    formControlName="min"
                    class="metric-field-card__range-input"
                    placeholder="0"
                    [readonly]="!editableMode"
                    [class.error]="isCustomMetricRangeInvalid(i)"
                  />
                </div>
                <span class="metric-field-card__range-sep">–</span>
                <div class="metric-field-card__range-field">
                  <label [for]="'modify-custom-metric-max-' + i" class="metric-field-card__range-label">Max</label>
                  <input
                    type="text"
                    inputmode="decimal"
                    [id]="'modify-custom-metric-max-' + i"
                    formControlName="max"
                    class="metric-field-card__range-input"
                    placeholder="1"
                    [readonly]="!editableMode"
                    [class.error]="isCustomMetricRangeInvalid(i)"
                  />
                </div>
              </div>
              <button
                *ngIf="editableMode"
                type="button"
                class="metric-field-card__remove"
                (click)="removeCustomMetric(i)"
              >
                Remove
              </button>
              <div *ngIf="isCustomMetricInvalid(i)" class="metric-field-card__error">
                {{ getCustomMetricError(i) }}
              </div>
              <div *ngIf="isCustomMetricRangeInvalid(i)" class="metric-field-card__error">
                {{ getCustomMetricRangeError(i) }}
              </div>
            </div>
          </div>
          <button *ngIf="editableMode" type="button" class="dataset-metrics-section__add-btn" (click)="addCustomMetric()">
            Add metric
          </button>
        </section>

        <!-- Primary & secondary metric selection -->
        <section class="dataset-details-section" aria-labelledby="modify-dataset-metric-selection-heading">
          <h3 id="modify-dataset-metric-selection-heading" class="dataset-details-section__title">Primary &amp; secondary metrics</h3>
          <div class="dataset-details-grid">
            <div
              *ngFor="let field of datasetMetricSelectionFields"
              class="dataset-detail-field"
              [class.dataset-detail-field--filled]="isDetailFieldFilled(field.key)"
              [class.dataset-detail-field--invalid]="isDetailFieldInvalid(field.key)"
            >
              <label [for]="field.key" class="dataset-detail-field__label">
                {{ field.label }} <span class="required">*</span>
              </label>
              <div class="custom-dropdown-wrapper" *ngIf="editableMode; else modifyMetricDetailReadonly">
                <div
                  class="custom-dropdown-trigger"
                  [class.error]="isDetailFieldInvalid(field.key)"
                  [class.open]="isMetricDetailDropdownOpen(field.key)"
                  (click)="toggleMetricDetailDropdown(field.key)"
                >
                  <div class="trigger-content">
                    <span *ngIf="!metadataForm.get(field.key)?.value" class="placeholder">{{ field.placeholder }}</span>
                    <span *ngIf="metadataForm.get(field.key)?.value" class="selected-value">{{ getMetricDetailLabel(field.key) }}</span>
                    <svg class="dropdown-arrow" [class.open]="isMetricDetailDropdownOpen(field.key)" width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1 1L6 6L11 1" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </div>
                </div>
                <div class="custom-dropdown-panel" *ngIf="isMetricDetailDropdownOpen(field.key)">
                  <div
                    *ngFor="let opt of metricDropdownOptions"
                    class="dropdown-option"
                    [class.selected]="isMetricDetailSelected(field.key, opt.key)"
                    (click)="selectMetricDetail(field.key, opt.key)"
                  >
                    <div class="checkbox-wrapper">
                      <div class="checkbox-custom" [class.checked]="isMetricDetailSelected(field.key, opt.key)"></div>
                    </div>
                    <span class="option-label">{{ opt.label }}</span>
                  </div>
                  <div *ngIf="metricDropdownOptions.length === 0" class="dropdown-panel-empty">
                    Name a benchmark metric above first.
                  </div>
                </div>
              </div>
              <ng-template #modifyMetricDetailReadonly>
                <input type="text" class="form-input" [value]="getMetricDetailLabel(field.key)" disabled readonly />
              </ng-template>
              <div *ngIf="isDetailFieldInvalid(field.key)" class="dataset-detail-field__error">
                {{ getDetailFieldError(field.key) }}
              </div>
            </div>
          </div>
        </section>

        <!-- Distribution plots -->
        <section class="dataset-details-section dataset-plots-section" aria-labelledby="modify-dataset-plots-heading">
          <div class="dataset-plots-section__header">
            <div>
              <h3 id="modify-dataset-plots-heading" class="dataset-details-section__title">
                Dataset statistics plots
                <span class="dataset-plots-section__optional">Optional</span>
              </h3>
              <p class="dataset-plots-section__subtitle">
                Configure bar charts shown on the dataset details page.
              </p>
            </div>
            <button *ngIf="editableMode" type="button" class="dataset-plots-section__add-btn" (click)="addDistributionPlot()">
              Add plot
            </button>
          </div>

          <p *ngIf="!hasDistributionPlots" class="dataset-plots-section__empty">
            No plots added yet.
          </p>

          <div formArrayName="distributionPlots" class="dataset-plots-section__list">
            <article
              *ngFor="let plot of distributionPlots.controls; let plotIndex = index"
              class="dataset-plot-card"
              [formGroupName]="plotIndex"
            >
              <div class="dataset-plot-card__header">
                <h4 class="dataset-plot-card__title">Plot {{ plotIndex + 1 }}</h4>
                <button *ngIf="editableMode" type="button" class="dataset-plot-card__remove" (click)="removeDistributionPlot(plotIndex)">
                  Remove plot
                </button>
              </div>

              <div class="dataset-details-grid">
                <div class="dataset-detail-field">
                  <label class="dataset-detail-field__label" [for]="'modify-plot-label-' + plotIndex">Plot label</label>
                  <input
                    [id]="'modify-plot-label-' + plotIndex"
                    type="text"
                    formControlName="label"
                    class="dataset-detail-field__input"
                    placeholder="e.g. Label, Sex, Study setting"
                    [readonly]="!editableMode"
                  />
                </div>
                <div class="dataset-detail-field">
                  <label class="dataset-detail-field__label" [for]="'modify-plot-total-' + plotIndex">Total</label>
                  <input
                    [id]="'modify-plot-total-' + plotIndex"
                    type="text"
                    formControlName="total"
                    class="dataset-detail-field__input"
                    placeholder="Optional total count"
                    [readonly]="!editableMode"
                  />
                </div>
                <div class="dataset-detail-field dataset-detail-field--wide">
                  <label class="dataset-detail-field__label" [for]="'modify-plot-note-' + plotIndex">Note</label>
                  <textarea
                    [id]="'modify-plot-note-' + plotIndex"
                    formControlName="note"
                    class="dataset-detail-field__textarea"
                    rows="2"
                    placeholder="Optional note shown below the chart"
                    [readonly]="!editableMode"
                  ></textarea>
                </div>
              </div>

              <div class="dataset-plot-card__categories">
                <div class="dataset-plot-card__categories-head">
                  <span class="dataset-plot-card__categories-title">Categories</span>
                  <button *ngIf="editableMode" type="button" class="dataset-plot-card__add-category" (click)="addPlotCategory(plotIndex)">
                    Add category
                  </button>
                </div>

                <div formArrayName="categories" class="dataset-plot-card__categories-list">
                  <div
                    *ngFor="let category of getPlotCategories(plotIndex).controls; let categoryIndex = index"
                    class="dataset-plot-category"
                    [formGroupName]="categoryIndex"
                  >
                    <div class="dataset-plot-category__fields">
                      <div class="dataset-detail-field">
                        <label class="dataset-detail-field__label">Name</label>
                        <input type="text" formControlName="name" class="dataset-detail-field__input" placeholder="e.g. Suspicious" [readonly]="!editableMode" />
                      </div>
                      <div class="dataset-detail-field">
                        <label class="dataset-detail-field__label">Count</label>
                        <input type="number" formControlName="count" class="dataset-detail-field__input" min="0" step="1" placeholder="0" [readonly]="!editableMode" />
                      </div>
                      <div class="dataset-detail-field dataset-detail-field--wide">
                        <label class="dataset-detail-field__label">Description</label>
                        <input type="text" formControlName="description" class="dataset-detail-field__input" placeholder="Optional tooltip description" [readonly]="!editableMode" />
                      </div>
                    </div>
                    <button
                      *ngIf="editableMode"
                      type="button"
                      class="dataset-plot-category__remove"
                      [disabled]="getPlotCategories(plotIndex).length <= 1"
                      (click)="removePlotCategory(plotIndex, categoryIndex)"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            </article>
          </div>
        </section>
      </ng-container>

      <!-- For Models -->
      <ng-container *ngIf="isModel()">
        <div class="form-fields">
          <div class="form-row">
            <div class="form-field col-6">
              <label for="short-description" class="form-label">
                Short Description <span class="required">*</span>
              </label>
              <div class="textarea-wrapper">
                <textarea
                  id="short-description"
                  formControlName="shortDescription"
                  class="form-textarea"
                  [rows]="editableMode ? 3 : 4"
                  [readonly]="!editableMode"
                  [class.error]="metadataForm.get('shortDescription')?.invalid && metadataForm.get('shortDescription')?.touched"
                ></textarea>
              </div>
              <div *ngIf="metadataForm.get('shortDescription')?.invalid && metadataForm.get('shortDescription')?.touched" class="error-message">
                <span *ngIf="metadataForm.get('shortDescription')?.errors?.['required']">Short description is required</span>
                <span *ngIf="metadataForm.get('shortDescription')?.errors?.['minlength']">Short description must be at least 50 characters</span>
                <span *ngIf="metadataForm.get('shortDescription')?.errors?.['maxlength']">Short description cannot exceed 200 characters</span>
              </div>
            </div>
            <div class="form-field col-6">
              <label for="long-description" class="form-label">
                Long Description <span class="required">*</span>
              </label>
              <div class="textarea-wrapper">
                <textarea
                  id="long-description"
                  formControlName="longDescription"
                  class="form-textarea"
                  [rows]="editableMode ? 6 : 4"
                  [readonly]="!editableMode"
                  [class.error]="metadataForm.get('longDescription')?.invalid && metadataForm.get('longDescription')?.touched"
                ></textarea>
              </div>
              <div *ngIf="metadataForm.get('longDescription')?.invalid && metadataForm.get('longDescription')?.touched" class="error-message">
                <span *ngIf="metadataForm.get('longDescription')?.errors?.['required']">Long description is required</span>
                <span *ngIf="metadataForm.get('longDescription')?.errors?.['minlength']">Long description must be at least 50 characters</span>
                <span *ngIf="metadataForm.get('longDescription')?.errors?.['maxlength']">Long description cannot exceed 2000 characters</span>
              </div>
            </div>
          </div>
        </div>
        <div class="form-fields">
          <div class="form-row">
            <div class="form-field col-6">
              <label for="industry" class="form-label">
                Industry <span class="required">*</span>
              </label>
              <input
                type="text"
                id="industry"
                formControlName="industry"
                class="form-input"
                [readonly]="!editableMode"
                [class.error]="metadataForm.get('industry')?.invalid && metadataForm.get('industry')?.touched"
              />
              <div *ngIf="metadataForm.get('industry')?.invalid && metadataForm.get('industry')?.touched" class="error-message">
                Industry is required
              </div>
            </div>
            <div class="form-field col-6">
              <label for="license" class="form-label">
                License <span class="required">*</span>
              </label>
              <input
                type="text"
                id="license"
                formControlName="license"
                class="form-input"
                [readonly]="!editableMode"
                [class.error]="metadataForm.get('license')?.invalid && metadataForm.get('license')?.touched"
              />
              <div *ngIf="metadataForm.get('license')?.invalid && metadataForm.get('license')?.touched" class="error-message">
                License is required
              </div>
            </div>
          </div>
        </div>
      </ng-container>
    </form>

    <!-- Additional Info Form -->
    <form [formGroup]="additionalInfoForm">
      <div class="form-fields">
        <div class="form-row">
          <div class="form-field col-6" *ngIf="!isModel()">
            <label for="dataset-type" class="form-label">
              Dataset Type <span class="required">*</span>
            </label>
            <div class="custom-dropdown-wrapper" *ngIf="editableMode">
              <div
                class="custom-dropdown-trigger"
                [class.error]="additionalInfoForm.get('datasetType')?.invalid && additionalInfoForm.get('datasetType')?.touched"
                [class.open]="isDatasetTypeDropdownOpen"
                (click)="toggleDatasetTypeDropdown()"
              >
                <div class="trigger-content">
                  <span class="selected-value">{{ getSelectedDatasetTypeLabel() }}</span>
                  <svg class="dropdown-arrow" [class.open]="isDatasetTypeDropdownOpen" width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 1L6 6L11 1" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
              </div>
              <div class="custom-dropdown-panel" *ngIf="isDatasetTypeDropdownOpen">
                <div
                  *ngFor="let option of datasetTypeOptions"
                  class="dropdown-option"
                  [class.selected]="isDatasetTypeSelected(option.value)"
                  (click)="selectDatasetType(option)"
                >
                  <div class="checkbox-wrapper">
                    <div class="checkbox-custom" [class.checked]="isDatasetTypeSelected(option.value)"></div>
                  </div>
                  <span class="option-label">{{ option.label }}</span>
                </div>
              </div>
            </div>
            <input
              *ngIf="!editableMode"
              type="text"
              id="dataset-type"
              formControlName="datasetType"
              class="form-input"
              readonly
            />
            <div *ngIf="additionalInfoForm.get('datasetType')?.invalid && additionalInfoForm.get('datasetType')?.touched" class="error-message">
              Dataset type is required
            </div>
          </div>
          <div class="form-field col-6">
            <label for="file-format" class="form-label">
              File Format <span class="required">*</span>
            </label>
            <div class="custom-dropdown-wrapper" *ngIf="editableMode">
              <div
                class="custom-dropdown-trigger"
                [class.error]="additionalInfoForm.get('fileFormat')?.invalid && additionalInfoForm.get('fileFormat')?.touched"
                [class.open]="isFileFormatDropdownOpen"
                (click)="toggleFileFormatDropdown()"
              >
                <div class="trigger-content">
                  <span class="selected-value">{{ getSelectedFileFormatLabel() }}</span>
                  <svg class="dropdown-arrow" [class.open]="isFileFormatDropdownOpen" width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 1L6 6L11 1" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
              </div>
              <div class="custom-dropdown-panel" *ngIf="isFileFormatDropdownOpen">
                <div
                  *ngFor="let option of fileFormatOptions"
                  class="dropdown-option"
                  [class.selected]="isFileFormatSelected(option.value)"
                  (click)="selectFileFormat(option)"
                >
                  <div class="checkbox-wrapper">
                    <div class="checkbox-custom" [class.checked]="isFileFormatSelected(option.value)"></div>
                  </div>
                  <span class="option-label">{{ option.label }}</span>
                </div>
              </div>
            </div>
            <input
              *ngIf="!editableMode"
              type="text"
              id="file-format"
              formControlName="fileFormat"
              class="form-input"
              readonly
            />
            <div *ngIf="additionalInfoForm.get('fileFormat')?.invalid && additionalInfoForm.get('fileFormat')?.touched" class="error-message">
              File format is required
            </div>
          </div>
        </div>
        <div class="form-row">
          <div class="form-field" [class.col-6]="hasDataSource()" [class.col-12]="!hasDataSource()">
            <label for="tags" class="form-label">
              Tags <span class="required">*</span>
            </label>
            <div class="tags-input-wrapper" *ngIf="editableMode">
              <div class="tags-input-container">
                <input
                  type="text"
                  id="tags"
                  class="tags-input"
                  placeholder="Enter a tag"
                  [(ngModel)]="tagInput"
                  [ngModelOptions]="{standalone: true}"
                  (keydown)="onTagInputKeydown($event)"
                />
                <button
                  type="button"
                  class="tags-add-button"
                  (click)="addTag()"
                  [disabled]="!tagInput || tagInput.trim().length === 0"
                  title="Add tag"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 3V13M3 8H13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
              </div>
              <div class="tags-container" *ngIf="getTags().length > 0">
                <span *ngFor="let tag of getTags()" class="tag-chip" [class.tag-chip--duplicate]="isTagDuplicate(tag)">
                  {{ tag }}
                  <svg
                    class="tag-chip-close"
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    (click)="removeTag(tag)"
                  >
                    <path d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </span>
              </div>
              <div *ngIf="getTags().length === 0" class="no-tags-message">
                No tags added
              </div>
              <div *ngIf="additionalInfoForm.get('tags')?.invalid && additionalInfoForm.get('tags')?.touched" class="error-message">
                At least one tag is required
              </div>
            </div>
            <div *ngIf="!editableMode">
              <div class="tags-container" *ngIf="getTags().length > 0">
                <span *ngFor="let tag of getTags()" class="tag-chip">
                  {{ tag }}
                </span>
              </div>
              <div *ngIf="getTags().length === 0" class="no-tags-message">
                No tags added
              </div>
            </div>
          </div>
          <div class="form-field col-6" *ngIf="hasDataSource()">
            <label class="form-label">
              Data Source
              <span class="required" *ngIf="isModel()">*</span>
            </label>
            <input
              type="url"
              formControlName="sampleDataUrl"
              class="form-input"
              [readonly]="!editableMode"
            />
          </div>
        </div>
      </div>
    </form>

  </div>

  <!-- Submit/Cancel Buttons (outside the scrolling area so they stay visible) -->
  <div class="form-actions" *ngIf="metadataForm && additionalInfoForm">
    <button type="button" class="btn btn-secondary" (click)="onCancel()">Cancel</button>
    <button
      type="button"
      class="btn btn-primary"
      (click)="onSaveChangesClick()"
      [disabled]="isSaveButtonDisabled"
    >
      Save Changes
    </button>
  </div>
</div>

<!-- Original Preview Mode (for stepper flow) -->
<ng-container *ngIf="!editableMode">
<app-header [forceLightBackground]="true"></app-header>
<div class="provider-dataset">
  <div class="provider-dataset--layout">
    <app-org-admin-sidebar *ngIf="isOrgAdminRoute" class="provider-dataset--sidebar"></app-org-admin-sidebar>
    <app-provider-sidebar *ngIf="!isOrgAdminRoute" class="provider-dataset--sidebar"></app-provider-sidebar>
    <div class="provider-dataset--content">
      <app-page-header></app-page-header>
      <div class="dataset-container">
        <!-- Step 3 Template -->
        <ng-template #step3Template>
          <div class="step-content">
            <h2 class="step-title">Preview Metadata</h2>
            
            <!-- Dataset Information Fields (Disabled) -->
            <div *ngIf="datasetData" class="form-fields">
              <div class="form-row">
                <!-- Title Field (Disabled) -->
                <div class="form-field col-6">
                  <label for="dataset-title" class="form-label">
                    Title <span class="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="dataset-title"
                    [value]="datasetData.title"
                    class="form-input"
                    disabled
                  />
                </div>

                <!-- Permission Field (Disabled) -->
                <div class="form-field col-6">
                  <label for="dataset-permission" class="form-label">
                    Permission <span class="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="dataset-permission"
                    [value]="getPermissionDisplayLabel(datasetData.permission)"
                    class="form-input"
                    disabled
                  />
                </div>
              </div>
              
              <!-- HR Line -->
              <hr class="form-divider" />
            </div>

            <!-- Step 2: Metadata Information (Read-only) -->
            <form [formGroup]="metadataForm">
              <!-- For Datasets: All Metadata Fields -->
              <ng-container *ngIf="!isModel()">
                <!-- Description Fields -->
                <div class="form-fields">
                  <div class="form-row">
                    <!-- Short Description -->
                    <div class="form-field col-6">
                      <label for="short-description" class="form-label">
                        Short Description <span class="required">*</span>
                      </label>
                      <div class="textarea-wrapper">
                        <textarea
                          id="short-description"
                          formControlName="shortDescription"
                          class="form-textarea"
                          [rows]="editableMode ? 3 : 4"
                          readonly
                        ></textarea>
                      </div>
                    </div>

                    <!-- Long Description -->
                    <div class="form-field col-6">
                      <label for="long-description" class="form-label">
                        Long Description <span class="required">*</span>
                      </label>
                      <div class="textarea-wrapper">
                        <textarea
                          id="long-description"
                          formControlName="longDescription"
                          class="form-textarea"
                          [rows]="editableMode ? 3 : 4"
                          readonly
                        ></textarea>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Year Range and Geo Coverage Fields -->
                <div class="form-fields">
                  <div class="form-row">
                    <!-- Year Range Field -->
                    <div class="form-field col-6">
                      <label for="year-range" class="form-label">
                        Year Range <span class="required">*</span>
                      </label>
                      <app-year-range-picker
                        formControlName="yearRange"
                        placeholder="Choose year range"
                        styleClass="form-select"
                      ></app-year-range-picker>
                    </div>

                    <!-- Geo Coverage Field -->
                    <div class="form-field col-6">
                      <label for="geo-coverage" class="form-label">
                        Geo Coverage <span class="required">*</span>
                      </label>
                      <input
                        type="text"
                        id="geo-coverage"
                        formControlName="geoCoverage"
                        class="form-input"
                        readonly
                      />
                    </div>
                  </div>
                </div>

                <!-- Resource Server and Upload Frequency Fields -->
                <div class="form-fields">
                  <div class="form-row">
                    <!-- Resource Server Field -->
                    <div class="form-field col-6">
                      <label for="resource-server" class="form-label">
                        Resource Server <span class="required">*</span>
                      </label>
                      <div class="custom-dropdown-wrapper">
                        <div class="custom-dropdown-trigger disabled">
                          <div class="trigger-content">
                            <span class="selected-value">
                              {{ getResourceServerDisplay(metadataForm.get('resourceServer')?.value) || 'Not selected' }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Upload Frequency Field -->
                    <div class="form-field col-6">
                      <label for="upload-frequency" class="form-label">
                        Upload Frequency <span class="required">*</span>
                      </label>
                      <div class="custom-dropdown-wrapper">
                        <div class="custom-dropdown-trigger disabled">
                          <div class="trigger-content">
                            <span class="selected-value">
                              {{ getUploadFrequencyLabel(metadataForm.get('uploadFrequency')?.value) || 'Not selected' }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- License Field -->
                <div class="form-fields">
                  <div class="form-row">
                    <div class="form-field col-6">
                      <label for="license" class="form-label">
                        License <span class="required">*</span>
                      </label>
                      <div class="custom-dropdown-wrapper">
                        <div class="custom-dropdown-trigger disabled">
                          <div class="trigger-content">
                            <span class="selected-value">
                              {{ getLicenseLabel(metadataForm.get('license')?.value) || 'Not selected' }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Other License Input (shown when "Other" is selected) -->
                    <div 
                      class="form-field col-6" 
                      *ngIf="isOtherLicenseSelected()"
                    >
                      <label for="other-license" class="form-label">
                        Specify License <span class="required">*</span>
                      </label>
                      <input
                        type="text"
                        id="other-license"
                        formControlName="otherLicense"
                        class="form-input"
                        readonly
                      />
                    </div>
                  </div>
                </div>
              </ng-container>

              <!-- For Models: Description Fields, Industry and License -->
              <ng-container *ngIf="isModel()">
                <!-- Description Fields -->
                <div class="form-fields">
                  <div class="form-row">
                    <!-- Short Description -->
                    <div class="form-field col-6">
                      <label for="short-description" class="form-label">
                        Short Description <span class="required">*</span>
                      </label>
                      <div class="textarea-wrapper">
                        <textarea
                          id="short-description"
                          formControlName="shortDescription"
                          class="form-textarea"
                          [rows]="editableMode ? 3 : 4"
                          readonly
                        ></textarea>
                      </div>
                    </div>

                    <!-- Long Description -->
                    <div class="form-field col-6">
                      <label for="long-description" class="form-label">
                        Long Description <span class="required">*</span>
                      </label>
                      <div class="textarea-wrapper">
                        <textarea
                          id="long-description"
                          formControlName="longDescription"
                          class="form-textarea"
                          [rows]="editableMode ? 3 : 4"
                          readonly
                        ></textarea>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Industry and License Fields -->
                <div class="form-fields">
                  <div class="form-row">
                    <!-- Industry Field -->
                    <div class="form-field col-6">
                      <label for="industry" class="form-label">
                        Industry <span class="required">*</span>
                      </label>
                      <div class="custom-dropdown-wrapper">
                        <div class="custom-dropdown-trigger disabled">
                          <div class="trigger-content">
                            <span class="selected-value">
                              {{ getIndustryLabel(metadataForm.get('industry')?.value) || 'Not selected' }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- License Field -->
                    <div class="form-field col-6">
                      <label for="license" class="form-label">
                        License <span class="required">*</span>
                      </label>
                      <div class="custom-dropdown-wrapper">
                        <div class="custom-dropdown-trigger disabled">
                          <div class="trigger-content">
                            <span class="selected-value">
                              {{ getLicenseLabel(metadataForm.get('license')?.value) || 'Not selected' }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </ng-container>
            </form>

            <!-- Step 3: Additional Information (Read-only) -->
            <form [formGroup]="additionalInfoForm">
              <div class="form-fields">
                <div class="form-row">
                  <!-- Dataset / Model Type Field -->
                  <div class="form-field col-6">
                    <label for="dataset-type" class="form-label">
                      {{ isModel() ? 'Model Type' : 'Dataset Type' }}
                      <span class="required">*</span>
                    </label>
                    <div class="custom-dropdown-wrapper">
                      <div class="custom-dropdown-trigger disabled">
                        <div class="trigger-content">
                          <span class="selected-value">
                            {{ getDatasetTypeLabel(additionalInfoForm.get('datasetType')?.value) || 'Not selected' }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- File Format Field -->
                  <div class="form-field col-6">
                    <label for="file-format" class="form-label">
                      File Format <span class="required">*</span>
                    </label>
                    <div class="custom-dropdown-wrapper">
                      <div class="custom-dropdown-trigger disabled">
                        <div class="trigger-content">
                          <span class="selected-value">
                            {{ getFileFormatLabel(additionalInfoForm.get('fileFormat')?.value) || 'Not selected' }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Tags and Data Source Row -->
                <div class="form-row">
                  <!-- Tags Field -->
                  <div class="form-field" [class.col-6]="hasDataSource()" [class.col-12]="!hasDataSource()">
                    <label for="tags" class="form-label">
                      Tags <span class="required">*</span>
                    </label>
                    <div class="tags-container" *ngIf="getTags().length > 0">
                      <span
                        *ngFor="let tag of getTags()"
                        class="tag-chip"
                      >
                        {{ tag }}
                      </span>
                    </div>
                    <div *ngIf="getTags().length === 0" class="no-tags-message">
                      No tags added
                    </div>
                  </div>

                  <!-- Data Source Field - Only show if not empty or required for models -->
                  <div class="form-field col-6" *ngIf="hasDataSource()">
                    <label class="form-label">
                      Data Source
                      <span class="required" *ngIf="isModel()">*</span>
                    </label>
                    <input
                      type="url"
                      formControlName="sampleDataUrl"
                      class="form-input"
                      readonly
                    />
                  </div>
                </div>

                <!-- Data Descriptor Field (only for datasets) - Only show if there are descriptors -->
                @if (!isModel() && dataDescriptorsList.length > 0) {
                  <div class="form-field">
                    <label class="form-label">Data Descriptor</label>
                    <div class="descriptor-table-wrapper">
                      <table class="descriptor-table">
                        <thead>
                          <tr>
                            <th>Attribute Name</th>
                            <th>Parameter Type</th>
                            <th>Schema Type</th>
                          </tr>
                        </thead>
                        <tbody>
                          @for (descriptor of dataDescriptorsList; track $index) {
                            <tr>
                              <td>{{ descriptor.attributeName }}</td>
                              <td>{{ descriptor.parameterType }}</td>
                              <td>
                                <span class="schema-badge">{{ descriptor.schemaType }}</span>
                              </td>
                            </tr>
                          } @empty {
                            <tr>
                              <td colspan="3" class="descriptor-empty-row">No data descriptors available</td>
                            </tr>
                          }
                        </tbody>
                      </table>
                    </div>
                  </div>
                }
              </div>
            </form>
          </div>
        </ng-template>

        <!-- Stepper Component -->
        <app-stepper
          [steps]="stepperStepsWithLabel"
          [currentStep]="currentStep"
          (stepChange)="onStepChange($event)"
          (next)="onNext()"
          (previous)="onPrevious()"
        >        </app-stepper>
      </div>
    </div>
  </div>
</div>
</ng-container>